import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '../services/authService'
import { apiService, apiClient } from '../services/apiService'
import { analyticsService } from '../services/analyticsService'
import { SimpleSnackbar } from '../utils/SnackbarManager'
import { clearAllEnumCaches } from '../services/enumCacheService'
import { clearAllAmenityCaches } from '../services/amenityCacheService'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const userProfile = ref(null)
  const loading = ref(true)
  const error = ref(null)
  const roles = ref([])
  const showProfileCompletionDialog = ref(false)

  // Promise that resolves when initial auth check completes
  let _initResolve
  const initPromise = new Promise(resolve => { _initResolve = resolve })
  let _activePermissionCheck = null
  let _lastPermissionCheck = 0

  // Getters
  const userInfo = computed(() => {
    return authService.getUserInfo()
  })

  // Computed property for reactive authentication state
  const isAuthenticated = computed(() => {
    return !!user.value
  })

  const initialize = async () => {
    loading.value = true
    try {
      const account = await authService.initialize()
      if (account) {
        user.value = account
        // Fetch user profile from API before loading roles
        try {
          await fetchUserProfile()
        } catch (e) {
          console.warn('Failed to fetch user profile during init:', e)
        }
        // Load roles from access token after user is set
        try {
          roles.value = await authService.getRoles('api')
          analyticsService.setUserRole(roles.value)
        } catch (e) {
          console.warn('Failed to load roles during init:', e)
          roles.value = []
        }
      }
    } catch (error) {
      console.error('Auth initialization failed:', error)
      user.value = null
      userProfile.value = null
      roles.value = []
    } finally {
      loading.value = false
      _initResolve()
    }

    // Listen for logout from other tabs (storage event fires only in other tabs)
    window.addEventListener('storage', () => {
      if (!user.value) return // Already logged out in this tab

      const accounts = authService.msalInstance?.getAllAccounts() || []
      if (accounts.length === 0) {
        console.warn('Session ended in another tab — logging out')
        user.value = null
        userProfile.value = null
        roles.value = []
        window.location.href = '/login'
      }
    })
  }

  const login = async () => {
    loading.value = true
    error.value = null

    try {
      const account = await authService.login()
      user.value = account

      // Clear cached enum/amenity data to ensure fresh data for this session.
      // This runs when user explicitly clicks login. For returning users with
      // cached MSAL tokens, initialize() runs instead (which doesn't clear cache).
      // To handle that case, we also clear cache on logout.
      clearAllEnumCaches()
      clearAllAmenityCaches()
      // Fetch user profile from API before loading roles
      try {
        await fetchUserProfile()
      } catch (e) {
        console.warn('Failed to fetch user profile after login:', e)
      }
      // Refresh roles on login
      try {
        roles.value = await authService.getRoles('api')
        analyticsService.setUserRole(roles.value)
      } catch (e) {
        console.warn('Failed to load roles after login:', e)
        roles.value = []
      }
      return account
    } catch (err) {
      error.value = err.message || 'Login failed'
      console.error('Login failed:', err)
      user.value = null
      userProfile.value = null
      roles.value = []
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    error.value = null

    try {
      const didLogout = await authService.logout()
      if (didLogout) {
        user.value = null
        userProfile.value = null
        roles.value = []
        analyticsService.clearUser()
        // Clear cached enum/amenity data so next session fetches fresh data.
        // This ensures that if MSAL restores the session via initialize()
        // (which doesn't clear cache), the user still gets updated enums.
        // For users who close browser without logging out, the 1-hour TTL applies.
        clearAllEnumCaches()
        clearAllAmenityCaches()
      }
      return didLogout
    } catch (err) {
      error.value = err.message || 'Logout failed'
      console.error('Logout failed:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getAccessToken = async (scopeType = 'api') => {
    try {
      return await authService.getAccessToken(scopeType)
    } catch (err) {
      error.value = err.message || 'Failed to get access token'
      throw err
    }
  }

  const refreshToken = async () => {
    try {
      await authService.getAccessToken()
    } catch (err) {
      console.error('Token refresh failed:', err)
      // On session death (unrecoverable 401), force logout with message
      await forceLogoutWithMessage('Your session has expired. Please log in again.')
      throw err
    }
  }

  const getApiToken = async () => {
    return getAccessToken('api')
  }

  const getGraphToken = async () => {
    return getAccessToken('graph')
  }

  const clearError = () => {
    error.value = null
  }

  const loadRoles = async () => {
    try {
      roles.value = await authService.getRoles('api')
      return roles.value
    } catch (e) {
      console.warn('Failed to load roles:', e)
      roles.value = []
      return roles.value
    }
  }

  const hasRole = (role) => {
    if (role == null) return false
    if (!Array.isArray(roles.value)) return false

    return roles.value.some(r => typeof r === 'string' && r.toLowerCase() === String(role).toLowerCase())
  }

  const fetchUserProfile = async () => {
    try {
      const profile = await apiService.get('/Users/me')
      userProfile.value = profile

      // Initialize GA4 with user properties
      analyticsService.initUser(profile)

      // Check if profile needs completion
      if (profile && needsProfileCompletion(profile)) {
        showProfileCompletionDialog.value = true
      }

      return profile
    } catch (err) {
      // Handle 404 - user not found, log them out
      if (err.statusCode === 404) {
        console.warn('User profile not found (404) - logging out user')
        userProfile.value = null
        // Show error message and logout
        SimpleSnackbar.error('User not found. Please sign in again.')
        await logout()
      } else {
        console.error('Failed to fetch user profile:', err.message || err)
        userProfile.value = null
      }
      return null
    }
  }

  const checkPermissionChanges = async () => {
    // If a check is already in progress, return the existing promise
    if (_activePermissionCheck) return _activePermissionCheck

    // If we don't have a baseline profile yet, we can't detect changes meaningfully.
    // Returning early prevents false-positive logouts during app initialization.
    if (!userProfile.value) return { changed: false, reason: null }

    _activePermissionCheck = (async () => {
      try {
        const response = await apiClient.get('/Users/me', { _skipPermissionCheck: true })
        const freshProfile = response.data
        const freshRoles = await authService.getRoles('api', true)

        const currentOrgId = userProfile.value?.organisation?.id
        const freshOrgId = freshProfile?.organisation?.id
        const orgChanged = currentOrgId !== freshOrgId

        const currentRolesSorted = [...(roles.value || [])].sort()
        const freshRolesSorted = [...(freshRoles || [])].sort()
        const rolesChanged = JSON.stringify(currentRolesSorted) !== JSON.stringify(freshRolesSorted)

        _lastPermissionCheck = Date.now()

        if (orgChanged || rolesChanged) {
          return {
            changed: true,
            reason: rolesChanged ? 'role' : (orgChanged ? 'team' : null)
          }
        }

        return { changed: false, reason: null }
      } catch (e) {
        console.warn('Failed to check permission changes:', e)
        return { changed: false, reason: null }
      } finally {
        _activePermissionCheck = null
      }
    })()

    return _activePermissionCheck
  }

  const checkPermissionsOnNavigate = async () => {
    // Throttle checks on navigation to once per minute to reduce API load
    const oneMinute = 60 * 1000
    if (Date.now() - _lastPermissionCheck < oneMinute) {
      return false
    }

    try {
      const { changed, reason } = await checkPermissionChanges()
      if (changed) {
        const message = reason === 'team'
          ? 'Your team assignment has changed. Please log in again to continue.'
          : reason === 'role'
            ? 'Your role has been updated. Please log in again to continue.'
            : 'Your permissions have been updated. Please log in again to continue.'
        await forceLogoutWithMessage(message)
        return true
      }
    } catch (e) {
      console.warn('Permission change check failed on navigate:', e)
    }
    return false
  }

  const forceLogoutWithMessage = async (message) => {
    sessionStorage.setItem('auth_permission_message', message)
    try {
      await logout()
    } catch (e) {
      user.value = null
      userProfile.value = null
      roles.value = []
    }
    window.location.href = '/login'
  }

  const needsProfileCompletion = (profile) => {
    if (!profile) return false

    // Check if givenName, surname, or jobTitle is empty
    const givenNameEmpty = !profile.givenName || profile.givenName.trim() === ''
    const surnameEmpty = !profile.surname || profile.surname.trim() === ''
    const jobTitleEmpty = !profile.jobTitle || profile.jobTitle.trim() === ''

    // Check if terms and conditions have not been accepted
    const termsNotAccepted = !profile.termsAcceptedAt

    return givenNameEmpty || surnameEmpty || jobTitleEmpty || termsNotAccepted
  }

  const updateUserProfile = async (updates) => {
    try {
      if (!userProfile.value) {
        throw new Error('No user profile loaded')
      }

      // Only send the id and the fields being updated
      const payload = {
        id: userProfile.value.id,
        ...updates
      }

      const result = await apiService.patch('/Users/me', payload)
      userProfile.value = result
      showProfileCompletionDialog.value = false
      return result
    } catch (err) {
      console.error('Failed to update user profile:', err.message || err)
      throw err
    }
  }

  return {
    // State
    user,
    userProfile,
    loading,
    error,
    isAuthenticated,
    showProfileCompletionDialog,

    // Getters
    userInfo,

    // Actions
    initialize,
    login,
    logout,
    getAccessToken,
    refreshToken,
    getApiToken,
    getGraphToken,
    clearError,
    roles,
    loadRoles,
    hasRole,
    fetchUserProfile,
    updateUserProfile,
    needsProfileCompletion,
    checkPermissionChanges,
    checkPermissionsOnNavigate,
    forceLogoutWithMessage,
    initPromise,
  }
})
